import React, { useState, useEffect } from 'react';
import { StyledAdminLoginForm, StyledText } from '@/views/AdminLogin/AdminLogin.styled.ts';
import { useForm, SubmitHandler } from 'react-hook-form';
import { LoginSchema, LoginSchemaType } from '@/data/schema/loginschema.ts';
import { zodResolver } from '@hookform/resolvers/zod';
import useLogin from '@/hooks/useLogin.ts';
import AuthCookieHandler from '@/services/AuthCookieHandler';
import AuthInputField from '@/components/ui/InputField';
import AuthFormButton from '@/components/ui/AuthFormButton';
import { useNavigate, useParams } from 'react-router-dom';
import { StyledInputFieldError } from '@/components/ui/PersonalDetailEntry/PersonalDetailEntry.styled.ts';
import { toast } from 'react-toastify';

const AdminLoginPage: React.FunctionComponent = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const {
    register,
    handleSubmit,
    setError,
    formState: { errors },
  } = useForm<LoginSchemaType>({
    resolver: zodResolver(LoginSchema),
  });

  useEffect(() => {
    const was403Redirect = localStorage.getItem('login_403_redirect');
    if (was403Redirect === 'true') {
      setError('username', {
        type: 'manual',
        message: 'The username provided doesnt have access to the venue',
      });
      setError('password', {
        type: 'manual',
        message: 'Invalid credentials',
      });
      localStorage.removeItem('login_403_redirect');
    }
  }, [setError]);

  const login = useLogin({
    cb: () => setIsLoading(true),

    cbError: (error) => {
      setIsLoading(false);

      const errorField = error?.response?.data?.errors?.[0]?.field;
      const errorMessage = error?.response?.data?.errors?.[0]?.message;
      const displayMessage = Array.isArray(errorMessage) ? errorMessage[0] : errorMessage;

      toast.error(displayMessage || 'An error occurred');

      switch (errorField) {
        case 'non_field_errors':
          setError('username', {
            type: 'manual',
            message: displayMessage || 'Invalid credentials',
          });
          setError('password', {
            type: 'manual',
            message: ' ',
          });
          break;
        case 'username':
          setError('username', {
            type: 'manual',
            message: displayMessage || 'Invalid username',
          });
          break;
        case 'password':
          setError('password', {
            type: 'manual',
            message: displayMessage || 'Invalid password',
          });
          break;
        default:
          setError('username', {
            type: 'manual',
            message: 'An error occurred',
          });
          setError('password', {
            type: 'manual',
            message: 'An error occurred',
          });
          break;
      }
    },

    cbSuccess: (res) => {
      setIsLoading(false);

      if (!res) {
        toast.error('Login failed. Please try again.');
        setError('username', {
          type: 'manual',
          message: 'Login failed',
        });
        setError('password', {
          type: 'manual',
          message: 'Login failed',
        });
        return;
      }

      const hasSeatReservationAccess = res.features?.some(
        (feature) => feature.slug === 'seat-reservation',
      );

      if (!hasSeatReservationAccess) {
        toast.error('Access denied. You dont have access to this venue.');
        setError('username', {
          type: 'manual',
          message: 'The username doesnt have access to this venue.',
        });
        setError('password', {
          type: 'manual',
          message: ' ',
        });
        return;
      }

      const authHandler = new AuthCookieHandler();
      authHandler.saveAuth(res);
      navigate(`/qr/${id}/admin`, { replace: true });
    },
  });

  const onSubmit: SubmitHandler<LoginSchemaType> = ({ username, password }) => {
    login.mutate({ username, password });
  };

  return (
    <StyledAdminLoginForm onSubmit={handleSubmit(onSubmit)}>
      <StyledText>Sign in to continue</StyledText>

      <AuthInputField
        label="Email Address"
        register={register('username')}
        placeholder="Enter your email"
        isPassword={false}
        error={errors.username?.message}
      />
      {errors.username && <StyledInputFieldError>{errors.username.message}</StyledInputFieldError>}

      <AuthInputField
        placeholder="Enter Password"
        label="Password"
        register={register('password')}
        isPassword
        isPasswordVisible={isVisible}
        passwordVisibilityDispatcher={setIsVisible}
        error={errors.password?.message}
      />
      {errors.password && <StyledInputFieldError>{errors.password.message}</StyledInputFieldError>}

      <AuthFormButton label="Sign in" isLoading={isLoading} />
    </StyledAdminLoginForm>
  );
};

export default AdminLoginPage;
